import { useTranslate, HttpError } from '@refinedev/core';
import { Edit, useForm } from '@refinedev/antd';
import { Form, Input, Radio, Tabs, Divider, theme } from 'antd';
import type {
  PublisherDetailOut,
  PublisherUpdateIn,
} from '@/api/generated/schemas';
import { ModStatus } from '@/api/generated/schemas';
import { CollectionImageFields } from '@/components/common/CollectionImageFields';
import { SearchPopularityField } from '@/components/common/SearchPopularityField';
import { PublisherEnableHint } from '@/components/user_catalog/publishers/PublisherEnableHint';
import { usePublisherStanding } from '@/hooks/tenancy/usePublisherStanding';
import {
  CollectionSortingList,
  normalizeCollectionSorting,
} from '@/components/query-builder';

export const PublisherEdit = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps, query } = useForm<
    PublisherDetailOut,
    HttpError,
    PublisherUpdateIn
  >({
    resource: 'user_catalog.publishers',
    mutationMeta: { method: 'patch' },
  });
  const record = query?.data?.data;
  const { token } = theme.useToken();
  const modStatus = Form.useWatch('mod_status', formProps.form);
  const standing = usePublisherStanding(record?.id);

  const handleFinish = (values: PublisherUpdateIn) =>
    formProps.onFinish?.({
      ...values,
      publication_custom_sorting: normalizeCollectionSorting(
        values.publication_custom_sorting
      ),
    });

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical" onFinish={handleFinish}>
        <Tabs
          items={[
            {
              key: 'basic',
              label: t('base.sections.basic_info'),
              children: (
                <>
                  <Form.Item
                    name="name"
                    label={t('user_catalog.publishers.fields.name')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="description"
                    label={t('user_catalog.publishers.fields.description')}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <Divider>{t('base.sections.system_info')}</Divider>

                  <Form.Item
                    name="mod_status"
                    label={t('base.fields.mod_status')}
                    rules={[{ required: true }]}
                  >
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      options={Object.values(ModStatus).map((value) => ({
                        label: t(`base.mod_status.${value}`),
                        value,
                      }))}
                    />
                  </Form.Item>
                  {/* Enabling is checked against tenancy on save; say why it
                      would be refused before the user tries. */}
                  {modStatus === ModStatus.enabled &&
                    record?.mod_status !== ModStatus.enabled && (
                      <PublisherEnableHint
                        result={standing}
                        style={{ marginBottom: token.marginLG }}
                      />
                    )}
                </>
              ),
            },
            {
              key: 'collection',
              label: t('base.sections.collection_info'),
              children: (
                <>
                  <Form.Item
                    name="header"
                    label={t('user_catalog.publishers.fields.header')}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="content"
                    label={t('user_catalog.publishers.fields.content')}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <CollectionImageFields
                    coverUrl={record?.cover_url}
                    bannerUrl={record?.banner_url}
                  />

                  <Divider>{t('base.collection.sorting')}</Divider>
                  <Form.Item extra={t('base.collection.sorting_hint')}>
                    <CollectionSortingList />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'search',
              label: t('user_catalog.common.tabs.search'),
              children: <SearchPopularityField />,
            },
          ]}
        />
      </Form>
    </Edit>
  );
};
